// ════════════════════════════════════════════════════════════════════
// TERROR — THE POP WINDOW
// The rare things a Minerium hands you, said properly.
// ────────────────────────────────────────────────────────────────────
// caP'n, 2026-09-07:
//
//   *"I will add dialogue to the game, for now you just show up to
//    nodes, defeat the enemy, and move on lmfao ez pz. however, we
//    will have a few pop windows for special things of course like
//    earning Shards etc"*
//
// ── THE GAP THIS FILLS, AND IT IS A REAL ONE ────────────────────────
// `ResultRibbon` in WorldMap is ONE LINE and it RANKS. Read its own
// banner: it deliberately shows the rarest true thing and throws the
// rest away. That is correct for a ribbon and wrong for a payout — a
// player who cleared a Sol King, answered its riddle and found a child
// on the same win is told exactly one of those three happened.
//
// So the ribbon stays for the ordinary case ("The road opens") and
// anything RARE comes here instead, one window at a time, in a queue.
// Nothing is dropped and nothing is ranked away.
//
// ── PLACEHOLDER-SAFE, THE SAME WAY StoryCutscene IS ─────────────────
// `noticesFor()` returns [] for a result with nothing rare in it, and
// the caller renders nothing. A new rare thing is one row in the
// builder; a new KIND of rare thing is one entry in FACES.
// ════════════════════════════════════════════════════════════════════

import React, { useEffect, useCallback } from "react";
import { GM } from "./theme";

const G = {
  diamond: "◆", star4: "✦", moon: "☾",
  check: "✓", dash: "—", eye: "◉",
};

// ── THE FACES ───────────────────────────────────────────────────────
// glyph + colour per kind. A kind with no face falls back to gold,
// which is why an unknown kind renders rather than throwing.
const FACES = {
  shard:  { glyph: G.diamond, tone: "#ffd47a" },
  egg:    { glyph: G.star4,   tone: "#ff8fb4" },
  set:    { glyph: G.star4,   tone: "#ffe7b0" },
  child:  { glyph: G.moon,    tone: "#7fe3ff" },
  crest:  { glyph: G.diamond, tone: "#f2c14e" },
  tool:   { glyph: G.eye,     tone: "#c9a84c" },
  broke:  { glyph: G.dash,    tone: "#c98a8a" },
};
const face = (kind) => FACES[kind] || { glyph: G.star4, tone: GM.inkGoldBright };

/**
 * <WorldNotice notices={[{kind,title,body,foot}]} onClose={() => ...} />
 *
 * Renders the FIRST notice in the list. The caller shifts the queue in
 * `onClose`. Held that way on purpose: the queue lives with the screen
 * that owns the save, so a notice cannot outlive the map that made it.
 */
export default function WorldNotice({ notices, onClose }) {
  const n = (notices || [])[0];
  const close = useCallback(() => { if (onClose) onClose(); }, [onClose]);

  // Enter / Space / Escape / click all dismiss. Same keys as
  // StoryCutscene, so the two never fight over a keypress.
  useEffect(() => {
    if (!n) return;
    const onKey = (e) => {
      if (["Enter", " ", "Escape", "Spacebar"].includes(e.key)) {
        e.preventDefault(); e.stopPropagation(); close();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [n, close]);

  if (!n) return null;
  const f = face(n.kind);
  const rows = Array.isArray(n.body) ? n.body : (n.body ? [n.body] : []);

  return (
    <div
      data-chrome
      onClick={close}
      style={{
        position: "absolute", inset: 0, zIndex: 40, cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "radial-gradient(ellipse at center, rgba(3,2,10,.62), rgba(3,2,10,.9))",
        backdropFilter: "blur(3px)", pointerEvents: "auto",
      }}
    >
      <div style={{
        minWidth: 268, maxWidth: 420, padding: "22px 26px 18px",
        borderRadius: 14, border: `1px solid ${f.tone}55`,
        background: "linear-gradient(165deg,rgba(16,10,26,.97),rgba(6,4,14,.97))",
        boxShadow: `0 0 46px ${f.tone}22, 0 18px 46px rgba(0,0,0,.65)`,
        textAlign: "center",
      }}>
        <div style={{ fontSize: 26, color: f.tone, textShadow: `0 0 22px ${f.tone}88` }}>
          {f.glyph}
        </div>

        <div style={{
          marginTop: 8, fontFamily: GM.font, fontWeight: 900, fontSize: 13,
          letterSpacing: ".22em", textTransform: "uppercase", color: f.tone,
        }}>
          {n.title}
        </div>

        {rows.map((line, i) => (
          <div key={i} style={{
            marginTop: i === 0 ? 12 : 6, fontFamily: GM.fontBody || GM.font,
            fontSize: 13, lineHeight: 1.55, color: GM.vellumDim,
          }}>
            {line}
          </div>
        ))}

        {n.foot && (
          <div style={{
            marginTop: 12, fontFamily: GM.font, fontSize: 9, letterSpacing: ".26em",
            textTransform: "uppercase", color: GM.vellumFaint, opacity: .8,
          }}>
            {n.foot}
          </div>
        )}

        <div style={{
          marginTop: 16, fontFamily: GM.font, fontSize: 9, letterSpacing: ".3em",
          textTransform: "uppercase", color: GM.vellumFaint, opacity: .55,
        }}>
          {(notices.length > 1) ? `${notices.length - 1} more ${G.dash} tap` : "tap to close"}
        </div>
      </div>
    </div>
  );
}

// ── THE BUILDER ─────────────────────────────────────────────────────
// A WorldMap `result` (what recordNodeClear returned, plus win/nodeId)
// becomes zero or more windows. ORDER IS RAREST LAST, so the best
// thing is the one still on screen when the player stops tapping.
//
// ⚠ It deliberately does NOT fire on an ordinary win. "The road opens"
// is what every clear says and it belongs on the ribbon, where it does
// not cost a tap.
export function noticesFor(result, nodeName) {
  if (!result || !result.win) return [];
  const out = [];
  const where = nodeName ? ` at ${nodeName}` : "";

  if (result.shards) {
    out.push({
      kind: "shard",
      title: `${result.shards} Sol ${result.shards === 1 ? "Shard" : "Shards"}`,
      body: [
        "The keeper yields, and something the size of a held breath comes loose with it.",
        "A Sol Shard is not money. It is a key, and the doors it opens are in the sky.",
      ],
      foot: `taken${where}`,
    });
  }

  if (result.eggCrests) {
    out.push({
      kind: "crest",
      title: `${result.eggCrests} Divine ${result.eggCrests === 1 ? "Crest" : "Crests"}`,
      body: "The riddle answered. The Minerium pays for the hardest optional thing in it.",
      foot: "the reserve",
    });
  }

  if (result.prize) {
    out.push({
      kind: "egg",
      title: "The riddle answered",
      body: `${result.prize} coins, and the node remembers you did it the difficult way.`,
      foot: `egg${where}`,
    });
  }

  if (result.storySet) {
    const s = result.storySet;
    out.push({
      kind: "set",
      title: s.cosmetics ? "The Eye opens"
           : s.both ? `${s.label} ${G.dash} claimed, and seared`
           : s.tier === "mythic" ? `The Griddle finds you`
           : "The Minerium pays",
      body: s.cosmetics
        ? "His room, his coin, his light. No card in the game can be any of the three."
        : s.both
          ? `${s.label}, twice over ${G.dash} once plain, once in Mythic, wearing his foil.`
          : s.tier === "mythic"
            ? `${s.label}, in Mythic.`
            : `${s.label}.`,
      foot: s.tier === "mythic" ? "holo_vrown" : "fable",
    });
  }

  if (result.child) {
    out.push({
      kind: "child",
      title: "A trace",
      body: "It is not them. It is the second-best thing there is, and it is still warm.",
      foot: "the trail goes on",
    });
  }

  return out;
}

// ── THE TOOL WINDOWS ────────────────────────────────────────────────
// Not built from a `result` — a tool is won at a gate, and a tool
// BREAKS on the map, neither of which is a node clear.
export function noticeForTool(tool) {
  if (!tool) return null;
  return {
    kind: "tool",
    title: tool.name,
    body: [
      tool.line,
      ...(tool.price || []).map(p => p.line).filter(Boolean),
    ],
    foot: `lent by ${tool.lender || "someone"}`,
  };
}

export function noticeForBreak(tool) {
  if (!tool) return null;
  return {
    kind: "broke",
    title: `${tool.name} slips`,
    body: tool.broke || "Something looked up.",
    foot: "walk, and it closes over you again",
  };
}
